// Builds document-service fetch/documents requests for the v2 drilldown's Publications and Patents lists.
import { advisoryTitles } from './advisoryLabels';

// Document filter field and ids for a request subject ({ type, ids }): a person by author / inventor ids, an
// institution by affiliation / assignee ids, a funder by acknowledged funder ids (patents have no funders)
const SUBJECT_FIELDS = {
  person: { publications: ['author_ids', 'author'], patents: ['inventor_ids', 'inventor'] },
  institution: { publications: ['author_affiliation_ids', 'institution'], patents: ['assignee_ids', 'assignee'] },
  funder: { publications: ['funder_ids', 'funder'] },
};

function subjectFilter(subject, tab) {
  const [field, idKey] = SUBJECT_FIELDS[subject.type][tab];
  return { [field]: [...(subject.ids[idKey] || [])] };
}

// A nested filter whose subfields ({ field, values, exclude }) must all hold on the same entry, e.g. a person-institution
// pair on one author_affiliations entry. Boolean scans take it as a nested query clause; semantic scans as a raw nested
// filter (the semantic filter builder ANDs subfields within one nested clause, and sends exclude: true to must_not),
// mirroring documents/fetchEntityDocuments.
function nestedClause(path, subfields) {
  const clause = (sf) => ({ terms: { [`${path}.${sf.field}`]: sf.values } });
  const bool = { must: subfields.filter((sf) => !sf.exclude).map(clause) };
  const excluded = subfields.filter((sf) => sf.exclude);
  if (excluded.length) bool.must_not = excluded.map(clause);
  return { nested: { path, query: { bool } } };
}

function nestedRawFilter(path, subfields) {
  return { type: 'nested', path, subfields, must_match: true };
}

const pairSubfields = (authorIds, affiliationIds) => [
  { field: 'author_id', values: authorIds },
  { field: 'affiliation_id', values: affiliationIds },
];

// sharedWith (optional), from a View button:
//   { kind: 'affiliation', entityId }            documents listing the person at that institution
//   { kind: 'funder', entityId }                 documents acknowledging that funder (a funder subject's: as well)
//   { kind: 'collaborator', personId, entityId } documents listing that collaborator at that institution
//   { kind: 'researcher', personId }             institution subject: documents listing that researcher at this
//                                                institution; funder subject: documents by that researcher
//   { kind: 'institution', entityId }            documents also listing that institution, for any author (graph
//                                                selection; an institution's partner rows)
//   { kind: 'coauthor', personId }               documents the person co-wrote with that collaborator (graph selection)
export function buildPublicationsRequest({ subject, scope, filters, sharedWith, pagination, scanId }) {
  const semantic = !!(scope && scope.query && scope.scan_type === 'semantic');
  const scanType = scope && scope.query ? scope.scan_type || 'boolean' : 'boolean';
  let query = scope && scope.query ? JSON.parse(JSON.stringify(scope.query)) : null;
  const requestFilters = subjectFilter(subject, 'publications');
  // ANDs a clause with the subject filter: a raw filter for semantic scans, a query filter clause for boolean ones
  const addClause = (rawFilter, booleanClause) => {
    if (semantic) {
      requestFilters.raw_filters = [...(requestFilters.raw_filters || []), rawFilter];
    } else {
      query = query || {};
      query.query = query.query || {};
      const bool = (query.query.bool = query.query.bool || {});
      bool.filter = [...(bool.filter || []), booleanClause];
    }
  };
  const addNested = (path, subfields) => addClause(nestedRawFilter(path, subfields), nestedClause(path, subfields));

  if (filters.minYear != null || filters.maxYear != null) {
    requestFilters.publication_year = [{ min: filters.minYear, max: filters.maxYear }];
  }
  // Matches the tab counts: advisory types only narrow the list when "Advisories only" is on. An institution's or
  // funder's own advisories (its institution or funder ids, and a merged organization's funder and BlueLens records)
  // are on every one of its documents and aren't connections, so its documents need an advisory of those types on
  // some other entity.
  if (filters.advisoriesOnly && subject.type !== 'person') {
    const own = ['institution', 'funder', 'bluelens'].flatMap((key) => subject.ids[key] || []);
    addNested('advisories', [
      { field: 'advisory_type', values: advisoryTitles(filters.advisoryTypes) },
      { field: 'entity_id', values: own, exclude: true },
    ]);
  } else if (filters.advisoriesOnly) {
    requestFilters['advisories.advisory_type'] = advisoryTitles(filters.advisoryTypes);
  }

  // A second (AND) clause on a field the subject's own filter already uses (a funder's funder_ids)
  const addTerms = (field, values) =>
    addClause({ type: 'terms', field, values, must_match: true }, { terms: { [`${field}.keyword`]: values } });

  if (sharedWith && sharedWith.kind === 'funder' && subject.type === 'funder') {
    addTerms('funder_ids', [sharedWith.entityId]);
  } else if (sharedWith && sharedWith.kind === 'funder') {
    requestFilters.funder_ids = [sharedWith.entityId];
  } else if (sharedWith && sharedWith.kind === 'researcher' && subject.type === 'funder') {
    requestFilters.author_ids = [sharedWith.personId];
  } else if (sharedWith && sharedWith.kind === 'researcher') {
    addNested('author_affiliations', pairSubfields([sharedWith.personId], subject.ids.institution));
  } else if (sharedWith && sharedWith.kind === 'institution') {
    // An institution subject's own filter is on author_affiliation_ids too
    addTerms('author_affiliation_ids', [sharedWith.entityId]);
  } else if (sharedWith && sharedWith.kind === 'coauthor') {
    // author_ids holds a person subject; the collaborator must also be an author
    addTerms('author_ids', [sharedWith.personId]);
  } else if (sharedWith) {
    const authors = sharedWith.kind === 'collaborator' ? [sharedWith.personId] : [...subject.ids.author];
    addNested('author_affiliations', pairSubfields(authors, [sharedWith.entityId]));
  }

  return {
    scan_id: scanId || null,
    scan_type: scanType,
    query,
    pagination,
    filters: requestFilters,
    skip_aggs: true,
    source: 'publication',
    must_match: [],
  };
}

// Patents list. Patents carry no advisories (the store skips the request under "Advisories only"). sharedWith is
// { kind: 'assignee', entityId } from a View button on the inventor's Affiliations table.
export function buildPatentsRequest({ subject, scope, filters, sharedWith, pagination, scanId }) {
  const scoped = !!(scope && scope.query);
  const requestFilters = subjectFilter(subject, 'patents');
  if (filters.minYear != null || filters.maxYear != null) {
    requestFilters.filing_year = [{ min: filters.minYear, max: filters.maxYear }];
  }
  if (sharedWith && sharedWith.kind === 'assignee') {
    requestFilters.assignee_ids = [sharedWith.entityId];
  }
  return {
    scan_id: scanId || null,
    scan_type: scoped ? scope.scan_type || 'boolean' : 'boolean',
    query: scoped ? JSON.parse(JSON.stringify(scope.query)) : null,
    pagination,
    filters: requestFilters,
    skip_aggs: true,
    source: 'patent',
    must_match: [],
  };
}
